import type React from 'react';
import {StatChip} from '~/components/StatChip/StatChip';
import {COLORS} from '~/style/colors-ts';
import DollarCoinIcon from '~/icons/svg/DollarCoinIcon.svg?react';
import DbIcon from '~/icons/svg/DbIcon.svg?react';
import ChartIcon from '~/icons/svg/ChartIcon.svg?react';
import NoteIcon from '~/icons/svg/NoteIcon.svg?react';
import {findNode} from '../domain/exploreTree';
import type {Explore} from '../types/categoryProfileTypes';
import {KpiRow} from '../profile/components/KpiRow';

type Props = {data: Explore, selected: string, money: (v: number) => string};

/** KPI pills of Explore Categories (spec 3.3), for the selected scope or the whole workspace. */
export const ExploreKpis: React.FC<Props> = ({data, selected, money}) => {
    const kpis = data.kpis;
    if (!kpis) return null;
    const node = selected ? findNode(data.tree, selected) : undefined;
    const spend = node?.spend ?? kpis.totalSpend;
    const poSpend = node?.poSpend ?? kpis.poSpend;
    const poShare = node ? node.poSharePct : kpis.poSharePct;
    const leaves = node ? countLeaves(node.children) || 1 : kpis.leafCategories;
    return (
        <KpiRow>
            <StatChip icon={{element: <DollarCoinIcon/>, color: COLORS.success.main}} title="Total spend"
                      value={money(spend)} tooltip={{type: 'info', text: 'Last 12 months with data'}}/>
            <StatChip icon={{element: <ChartIcon/>, color: COLORS.secondary.main}} title="Categories"
                      value={leaves.toLocaleString()}
                      tooltip={{type: 'info', text: `Leaf categories · ${kpis.levels}-level taxonomy`}}/>
            <StatChip icon={{element: <DbIcon/>, color: COLORS.info.main}} title="Suppliers"
                      value={(node?.suppliers ?? kpis.suppliers).toLocaleString()}
                      tooltip={{type: 'info', text: node ? `Across ${node.name}` : 'Across all categories'}}/>
            {kpis.showSpendUnderManagement && <StatChip icon={{element: <NoteIcon/>, color: COLORS.primary.main}} title="Spend under management"
                                                        value={poShare === null ? '—' : `${money(poSpend)} · ${poShare.toFixed(0)}%`}
                                                        tooltip={{type: 'info', text: 'Spend on lines with a purchase order'}}/>}
            {kpis.priceModelTargetPct !== undefined && kpis.showPriceModelSpend && (
                <StatChip icon={{element: <ChartIcon/>, color: COLORS.warning.main}} title="Spend on price models"
                          value={kpis.priceModelSharePct === null || kpis.priceModelSharePct === undefined ? '—'
                              : `${kpis.priceModelSharePct.toFixed(0)}%`}
                          tooltip={{type: 'info', text: `${kpis.priceModels ?? 0} active price model${kpis.priceModels === 1 ? '' : 's'}`
                              + ` · ${kpis.priceModelMode === 'category' ? 'whole spend of the modelled categories'
                                  : 'unit price × annual volume of each model, up to its category\u2019s spend'}`
                              + ` · target ${kpis.priceModelTargetPct}% of all spend`}}/>
            )}
        </KpiRow>
    );
};

function countLeaves(nodes: Explore['tree']): number {
    return nodes.reduce((n, c) => n + (c.children.length ? countLeaves(c.children) : 1), 0);
}
